<template>
  <base-list
    ref="list"
    :items="items"
    :loading="loading"
    :page-mode="pageMode"
    :max-height="maxHeight"
    :item-size="itemSize"
    :hierarchy-map="hierarchyMap"
    :show-inactive="showInactive"
    :loop-pointer="loopPointer"
    :autopoint="autopoint"
    :items-postprocessing="itemsPostprocessing"
    :caption-field="captionField"
    @enter-item="!disabled && toggle($event)"
    @left-key="$emit('left-key', $event)"
    @right-key="$emit('right-key', $event)"
  >
    <template #before="props">
      <slot
        v-bind="props"
        name="before"
        :reset="reset"
      />
    </template>
    <template #loading>
      <slot name="loading" />
    </template>
    <template #not-found>
      <slot name="not-found" />
    </template>
    <template #item-header="props">
      <slot
        v-bind="props"
        name="item-header"
      />
    </template>
    <template #item-header-title="props">
      <slot
        v-bind="props"
        name="item-header-title"
      />
    </template>
    <template #item="props">
      <select-item-wrapper
        :deep-children="hierarchyMap.deepChildrenMap[props.item.id]"
        :selected="selectionMap[props.item.id]"
        :multiple="multiple"
        :value="selection"
        @click="toggle(props.item)"
      >
        <template #default="{ select, selected, hasSelectedChildren, hasChildren }">
          <slot
            name="item"
            v-bind="props"
            :selected="selected"
            :toggle="select"
            :has-selected-children="hasSelectedChildren"
            :available="hasChildren && onlyLeaf ? false : props.available"
          >
            <select-item
              :highlight="props.highlight"
              :selected="selected"
              :disabled="props.disabled"
              :deep="props.deep"
              :active="props.active"
              :available="hasChildren && onlyLeaf ? false : props.available"
              :bordered="Boolean(props.nextItem && props.nextItem.id)"
            >
              <template #default>
                <slot
                  v-bind="props"
                  name="item-title"
                >
                  <span
                    :title="props.item[captionField]"
                    v-html="props.item[`${captionField}_highlight`] || props.item[captionField]"
                  />
                </slot>
              </template>
              <template #icon>
                <slot
                  v-bind="props"
                  name="item-icon"
                  :selected="selected"
                />
              </template>
              <template #before-content>
                <slot
                  v-bind="props"
                  name="item-before-content"
                />
              </template>
              <template #after-content>
                <slot
                  v-bind="props"
                  name="item-after-content"
                />
              </template>
              <template #hint>
                <slot
                  v-bind="props"
                  name="item-hint"
                />
              </template>
              <template #subtitle>
                <slot
                  v-bind="props"
                  name="item-subtitle"
                />
              </template>
            </select-item>
          </slot>
        </template>
      </select-item-wrapper>
    </template>
    <template #after="props">
      <slot
        v-bind="props"
        name="after"
        :reset="reset"
      />
    </template>
    <template #after-list="props">
      <slot
        v-bind="props"
        name="after-list"
      />
    </template>
  </base-list>
</template>

<script>
import { toRefs, ref } from 'vue';
import SelectItem from '@/components/list-item/select-item.vue';
import BaseList from '@/shared/legacy/ui/base-list/base-list.vue';
import { useListSelection } from '@/shared/legacy/ui/base-list/composables/use-list-selection';
import SelectItemWrapper from '@/shared/legacy/ui/base-list/select-item-wrapper.vue';

/**
 * @deprecated !Переходим на новый единый селект!
 * @refactor Используйте один из приведённых ниже компонентов, в зависимости от задачи
 * @see src/shared/ui/base-list/base-select-list.vue
 * @see src/shared/ui/base-list/base-select-dropdown.vue
 */
export default {
  name: 'SelectList',
  components: {
    SelectItemWrapper,
    BaseList,
    SelectItem
  },
  props: {
    loading: BaseList.props.loading,
    pageMode: BaseList.props.pageMode,
    items: BaseList.props.items,
    itemSize: BaseList.props.itemSize,
    showInactive: BaseList.props.showInactive,
    maxHeight: BaseList.props.maxHeight,
    hierarchyMap: BaseList.props.hierarchyMap,
    itemsPostprocessing: BaseList.props.itemsPostprocessing,
    loopPointer: BaseList.props.loopPointer,
    autopoint: BaseList.props.autopoint,
    captionField: BaseList.props.captionField,
    multiple: Boolean,
    value: {
      type: [Array, String, Number, Boolean],
      default: undefined
    },
    maxValues: {
      type: Number,
      default: Infinity
    },
    disabled: Boolean,
    allowFolderSelect: Boolean,
    onlyLeaf: Boolean, // доступны для выбора только элементы без детей, P.S. названия совсем не говорящее...
    disableAutoCompress: Boolean
  },
  emits: ['change', 'reset', 'left-key', 'right-key'],
  setup(props, { emit }) {
    const { value, multiple, hierarchyMap, onlyLeaf, disableAutoCompress, maxValues, allowFolderSelect }
      = toRefs(props);
    const list = ref(null);
    const { toggle, reset, selectionMap, selection } = useListSelection(value, {
      multiple,
      maxValues,
      hierarchyMap,
      onlyLeaf,
      disableAutoCompress,
      allowFolderSelect,
      onChange(newValue) {
        emit('change', newValue);
      }
    });
    return {
      list,
      next() {
        list.value.handleNext();
      },
      prev() {
        list.value.handlePrev();
      },
      enter() {
        list.value.handleEnter();
      },
      reset: () => {
        reset();
        emit('reset');
      },
      toggle,
      selectionMap,
      selection
    };
  }
};
</script>

<i18n lang="json">{}</i18n>
